// src/components/Campaigns/CampaignsSection.js - DÜZELTİLMİŞ
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../Firebase';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './CampaignsSection.module.css';

const normalizeCampaign = (campaign) => {
    if (!campaign.pricingModel) {
        return {
            ...campaign,
            pricingModel: "fixed",
            fixedMonthlyPrice: campaign.newPrice || campaign.price,
            totalDuration: campaign.totalDuration || campaign.contractPeriod || 24,
            priceTiers: [
                {
                    duration: campaign.totalDuration || campaign.contractPeriod || 24,
                    monthlyPrice: campaign.newPrice || campaign.price,
                    description: "Taahhüt boyunca"
                }
            ]
        };
    }
    return campaign;
};

const CampaignsSection = () => {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Firebase'den verileri çek
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Aktif sağlayıcıları getir
                const providersQuery = query(
                    collection(db, 'providers'),
                    where('isActive', '==', true)
                );
                const providersSnapshot = await getDocs(providersQuery);
                const providersList = [];

                for (const providerDoc of providersSnapshot.docs) {
                    const providerData = {
                        id: providerDoc.id,
                        ...providerDoc.data()
                    };

                    // Her sağlayıcının kampanyalarını getir
                    const campaignsQuery = query(
                        collection(db, `providers/${providerDoc.id}/campaigns`),
                        where('isActive', '==', true)
                    );
                    const campaignsSnapshot = await getDocs(campaignsQuery);

                    const campaignsList = campaignsSnapshot.docs.map(campaignDoc =>
                        normalizeCampaign({
                            id: campaignDoc.id,
                            providerId: providerDoc.id,
                            providerName: providerData.name,
                            providerLogo: providerData.logo,
                            ...campaignDoc.data()
                        })
                    );

                    // En az 1 kampanyası olan sağlayıcıları ekle
                    if (campaignsList.length > 0) {
                        providersList.push({
                            ...providerData,
                            campaigns: campaignsList, // Tüm kampanyaları sakla
                            featuredCampaign: campaignsList[0], // Mobil için ilk kampanya
                            totalCampaigns: campaignsList.length // Toplam kampanya sayısı
                        });
                    }
                }

                setProviders(providersList);
                setLoading(false);

            } catch (err) {
                console.error('Veri çekme hatası:', err);
                setError('Kampanyalar yüklenirken bir hata oluştu.');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Navigasyon fonksiyonları
    const handleCampaignClick = (providerId, campaignId) => {
        navigate(`/kampanyalar/${providerId}/${campaignId}`);
    };

    const handleProviderClick = (providerId) => {
        navigate(`/kampanyalar/`);
    };

    const handleViewAllClick = (providerId) => {
        navigate(`/kampanyalar/`);
    };

    const renderDesktopPriceInfo = (campaign) => {
        const normalizedCampaign = normalizeCampaign(campaign);

        if (normalizedCampaign.pricingModel === "fixed") {
            return (
                <div className={styles.priceInfo}>
                    {normalizedCampaign.originalPrice && normalizedCampaign.originalPrice !== normalizedCampaign.fixedMonthlyPrice && (
                        <div className={styles.originalPrice}>
                            {normalizedCampaign.originalPrice} TL
                        </div>
                    )}
                    <div className={styles.currentPrice}>
                        {normalizedCampaign.fixedMonthlyPrice} TL/ay
                    </div>
                    <div className={styles.priceNote}>
                        Taahhüt süresince sabit fiyat
                    </div>
                </div>
            );
        } else if (normalizedCampaign.pricingModel === "tiered" && normalizedCampaign.priceTiers?.length > 0) {
            return (
                <div className={styles.priceInfo}>
                    {normalizedCampaign.originalPrice && (
                        <div className={styles.originalPrice}>
                            {normalizedCampaign.originalPrice} TL
                        </div>
                    )}
                    <div className={styles.priceTiers}>
                        {normalizedCampaign.priceTiers.map((tier, index) => (
                            <div key={index} className={styles.priceTier}>
                                <span className={index === 0 ? styles.firstTierPrice : styles.tierPrice}>
                                    {tier.description}: {tier.monthlyPrice} TL/Ay
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className={styles.priceNote}>
                        {normalizedCampaign.totalDuration} ay taahhütlü
                    </div>
                </div>
            );
        }

        // Fallback
        return (
            <div className={styles.priceInfo}>
                <div className={styles.currentPrice}>
                    {campaign.newPrice || campaign.price || '0'} TL/ay
                </div>
                <div className={styles.priceNote}>
                    Kampanya fiyatı
                </div>
            </div>
        );
    };

    // Mobil için fiyat bilgisini render et
    const renderMobilePriceInfo = (campaign) => {
        const normalizedCampaign = normalizeCampaign(campaign);

        if (normalizedCampaign.pricingModel === "fixed") {
            return (
                <div className={styles.priceCard}>
                    {normalizedCampaign.originalPrice && normalizedCampaign.originalPrice !== normalizedCampaign.fixedMonthlyPrice && (
                        <div className={styles.priceRow}>
                            <span className={styles.originalPriceMobile}>
                                {normalizedCampaign.originalPrice} TL
                            </span>
                        </div>
                    )}
                    <div className={styles.priceRow}>
                        <span className={styles.currentPriceMobile}>
                            {normalizedCampaign.fixedMonthlyPrice}
                        </span>
                        <span className={styles.priceUnit}>TL/ay</span>
                    </div>
                    <div className={styles.priceNoteMobile}>
                        {normalizedCampaign.totalDuration} ay taahhütlü • Taahhüt süresince sabit
                    </div>
                </div>
            );
        } else if (normalizedCampaign.pricingModel === "tiered" && normalizedCampaign.priceTiers?.length > 0) {
            return (
                <div className={styles.tieredPricing}>
                    {normalizedCampaign.originalPrice && (
                        <div className={styles.priceRow} style={{ marginBottom: '1rem' }}>
                            <span className={styles.originalPriceMobile}>
                                Normal Fiyat: {normalizedCampaign.originalPrice} TL
                            </span>
                        </div>
                    )}
                    {normalizedCampaign.priceTiers.map((tier, index) => (
                        <div key={index} className={styles.tierRow}>
                            <span className={styles.tierDescription}>
                                {tier.description}
                            </span>
                            <span className={styles.tierPrice}>
                                {tier.monthlyPrice} TL/ay
                            </span>
                        </div>
                    ))}
                    <div className={styles.priceNoteMobile} style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                        Toplam {normalizedCampaign.totalDuration} ay taahhütlü
                    </div>
                </div>
            );
        }

        // Fallback
        return (
            <div className={styles.priceCard}>
                <div className={styles.priceRow}>
                    <span className={styles.currentPriceMobile}>
                        {campaign.newPrice || campaign.price || '0'}
                    </span>
                    <span className={styles.priceUnit}>TL/ay</span>
                </div>
                <div className={styles.priceNoteMobile}>
                    Kampanya fiyatı
                </div>
            </div>
        );
    };

    // Masaüstü için kampanya kartı component'i
    const DesktopCampaignCard = ({ campaign }) => {
        return (
            <motion.div
                className={styles.campaignCard}
                onClick={() => handleCampaignClick(campaign.providerId, campaign.id)}
                whileHover={{
                    scale: 1.05,
                    y: -5,
                    transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className={styles.cardHeader}>
                    <h3 className={styles.campaignTitle}>
                        {campaign.title || 'İnternet Kampanyası'}
                    </h3>
                </div>

                <div className={styles.cardBody}>
                    <div className={styles.speedInfo}>
                        <span className={styles.speedValue}>
                            {campaign.speed || '100'}
                        </span>
                        <span className={styles.speedUnit}>Mbps</span>
                    </div>

                    {renderDesktopPriceInfo(campaign)}
                </div>

                <div className={styles.cardFooter}>
                    <motion.button
                        className={styles.applyButton}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Hemen Başvur
                    </motion.button>
                </div>
            </motion.div>
        );
    };

    // Mobil için sağlayıcı kartı component'i
    const MobileProviderCard = ({ provider, index }) => {
        const campaign = provider.featuredCampaign;

        return (
            <motion.div
                className={styles.providerCardMobile}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -4 }}
            >
                {/* Sağlayıcı Header */}
                <div
                    className={styles.providerHeaderMobile}
                    onClick={() => handleProviderClick(provider.id)}
                    style={{ cursor: 'pointer' }}
                >
                    <img
                        src={provider.logo}
                        alt={provider.name}
                        className={styles.providerLogoMobile}
                        onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/60x60/33c5ff/ffffff?text=LOGO';
                        }}
                    />

                    <div className={styles.providerInfoMobile}>
                        <h3 className={styles.providerNameMobile}>
                            {provider.name}
                        </h3>
                        <p className={styles.providerDescriptionMobile}>
                            {provider.description || 'Fiber İnternet Hizmeti'}
                        </p>
                    </div>
                </div>

                {/* Öne Çıkan Kampanya */}
                <div className={styles.featuredCampaign}>
                    <h4 className={styles.campaignTitleMobile}>
                        {campaign.title || 'Özel İnternet Kampanyası'}
                    </h4>

                    {/* Kampanya Detayları */}
                    <div className={styles.campaignDetails}>
                        <div className={styles.detailCard}>
                            <div className={styles.detailValue}>
                                {campaign.speed}
                            </div>
                            <div className={styles.detailLabel}>
                                İnternet Hızı
                            </div>
                        </div>
                    </div>

                    {/* Fiyat Bilgisi */}
                    {renderMobilePriceInfo(campaign)}

                    {/* Butonlar */}
                    <div className={styles.buttonsContainer}>
                        <motion.button
                            className={styles.applyButtonMobile}
                            onClick={() => handleCampaignClick(provider.id, campaign.id)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <span>Hemen Başvur</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M13 7L18 12L13 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </motion.button>

                        {provider.totalCampaigns > 1 && (
                            <motion.button
                                className={styles.viewAllButtonMobile}
                                onClick={() => handleViewAllClick(provider.id)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span>
                                    {provider.totalCampaigns - 1} Kampanya Daha Gör
                                </span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </motion.button>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    // Masaüstü için sağlayıcı satırı component'i
    const DesktopProviderRow = ({ provider, index }) => {
        return (
            <div className={styles.providerRowWrapper}>
                <motion.div
                    className={styles.providerRow}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        duration: 0.6,
                        delay: index * 0.2
                    }}
                >
                    {/* Logo ve Sağlayıcı Bilgisi */}
                    <motion.div
                        className={styles.providerLogoSection}
                        onClick={() => handleProviderClick(provider.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <img
                            src={provider.logo}
                            alt={provider.name}
                            className={styles.providerLogo}
                            onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/120x120/33c5ff/ffffff?text=LOGO';
                            }}
                        />
                        <div className={styles.providerInfo}>
                            <h3 className={styles.providerName}>
                                {provider.name}
                            </h3>
                            <p className={styles.providerDescription}>
                                {provider.description || 'Fiber İnternet Hizmeti'}
                            </p>
                        </div>
                    </motion.div>

                    {/* Kampanyalar */}
                    <div className={styles.campaignsContainer}>
                        <div className={styles.campaignsGrid}>
                            <AnimatePresence>
                                {provider.campaigns && provider.campaigns.slice(0, 3).map((campaign, campaignIndex) => (
                                    <DesktopCampaignCard
                                        key={campaign.id}
                                        campaign={campaign}
                                        index={campaignIndex}
                                    />
                                ))}
                            </AnimatePresence>
                        </div>
                        <motion.button
                            className={styles.viewAllButton}
                            onClick={() => handleViewAllClick(provider.id)}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 + (index * 0.1) }}
                            whileHover={{
                                scale: 1.05,
                                y: -2
                            }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Tüm Kampanyaları Gör
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M5 12H19M19 12L12 5M19 12L12 19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.button>
                    </div>
                </motion.div>
            </div>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Kampanyalar yükleniyor...</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.errorContainer}>
                <p>{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className={styles.retryButton}
                >
                    Tekrar Dene
                </button>
            </div>
        );
    }

    // Empty state
    if (providers.length === 0) {
        return (
            <div className={styles.emptyContainer}>
                <p>Şu anda aktif kampanya bulunmamaktadır.</p>
                <p>Lütfen daha sonra tekrar kontrol edin.</p>
            </div>
        );
    }

    return (
        <section
            className={styles.campaignsSection}
            aria-labelledby="campaigns-title"
        >
            <header className={styles.sectionHeader}>
                {/* 🔴 H2 OLMALI */}
                <h2 id="campaigns-title" className={styles.sectionTitle}>
                    En Uygun Fiber İnternet Kampanyaları
                </h2>

                <p className={styles.sectionDescription}>
                    Türkiye genelindeki en uygun fiber internet kampanyalarını karşılaştırın.
                </p>
            </header>


            <div className={styles.providersContainer}>
                <AnimatePresence>
                    {providers.map((provider, index) => (
                        <div key={provider.id}>
                            {/* Masaüstü görünüm */}
                            <div className={styles.desktopOnly}>
                                <DesktopProviderRow
                                    provider={provider}
                                    index={index}
                                />
                            </div>

                            {/* Mobil görünüm */}
                            <div className={styles.mobileOnly}>
                                <MobileProviderCard
                                    provider={provider}
                                    index={index}
                                />
                            </div>
                        </div>
                    ))}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default CampaignsSection;